export const GAME_NAME = "NEON PURGE";
/** Bump on any breaking change to message formats. Client and server must match. */
export const PROTOCOL_VERSION = 1;
// ---------------------------------------------------------------------------
// Simulation / networking tunables (config-driven — never hardcode elsewhere)
// ---------------------------------------------------------------------------
/** Server physics tick rate (Hz).
 *  Raised 30 -> 60 (2026-10-21 `fluid`): the server now simulates and acks on
 *  the SAME 16.67 ms cadence the client predicts on, so the input->ack loop is
 *  1:1 (one input drained per tick, not two) and remote entities get a fresh
 *  authoritative sample twice as often — the standing "big lever" the BACKLOG
 *  deferred for want of a live rig. Everything tick-side is dt-driven
 *  (`tick(elapsedMs)` → `/1000`) so halving the period is integral-neutral; the
 *  only couplings that needed hand-tuning were the bot-think cadence (kept at
 *  ~30 Hz via `updateBotStagger`, so difficulty + CPU are unchanged) and the
 *  patch-relief floor. MUST be live-tested for server tick budget + uplink
 *  bandwidth before merge — it cannot be proven on the offline benches alone. */
export const SIM_TICK_RATE = 60;
/** Snapshot broadcast rate (Hz). Matches the sim tick: the healthy room flushes
 *  one patch per tick (see GunGameRoom.tickDrivenPatch), so this is only
 *  meaningful at the tick rate — a faster snapshot rate than the sim produces no
 *  new state. */
export const SNAPSHOT_RATE = 60; // 60Hz — one fresh sample per 16.67 ms sim tick
/** Client-side render delay for interpolating remote entities (ms).
 *  match.ts renders remotes in the past by `base + jitterPad`, where
 *    base = max(INTERP_BUFFER_MS, nominal * 1.6)   (nominal = measured patch gap)
 *  so at a healthy 30 Hz (nominal 33.3, nominal*1.6 = 53.3) THIS FLOOR sets the
 *  delay, and the ADAPTIVE jitterPad (`patchGapMax - nominal*1.4 - 20`) widens
 *  the window on bursty links on top of it. That delay is pure remote latency:
 *  how far in the past you see and shoot every other player.
 *
 *  History: 100 (static over-pad) -> 66 (2 snapshot intervals, the "textbook"
 *  value) -> 55. `agent/bench-interp-buffer.mjs` sweeps the floor under three
 *  link profiles: dropping it is a strict win on clean/wifi links (lower
 *  latency, lower position error, per-frame smoothness cv UNCHANGED, dead-reckon
 *  extrapolation still ~0 % of frames), because the jitterPad — not the static
 *  floor — is what actually absorbs jitter. 55 shaves 11 ms of remote render
 *  latency off the shipped 66 with dead-reckoning still well under 1 % of frames
 *  even on a clumping link.
 *
 *  COUPLING — do not drop below nominal*1.6 (= 53.3 ms at 30 Hz). This is ALSO
 *  the floor of the server lag-comp rewind (`lagCompRewindMs` = this + the
 *  shooter's measured RTT): the server rewinds a victim to where the shooter
 *  SAW them, which is exactly the client's `base` in the past. That identity
 *  holds only while THIS floor governs `base` (base == INTERP_BUFFER_MS). Go
 *  below 53.3 and nominal*1.6 governs the client while the server still rewinds
 *  by the raw floor — the two decouple and hitreg drifts off the crosshair.
 *  55 keeps 1.7 ms of margin above 53.3, so client render-delay and hitreg
 *  rewind stay locked together and both shrink — which also tightens the
 *  "killed behind cover" window by the same 11 ms. `agent/bench-lagcomp.mjs`
 *  asserts the rewind still lands dead-on. */
export const INTERP_BUFFER_MS = 55;
/** How far back the server keeps position history for lag-compensated hitreg (ms).
 *  Must cover the deepest honoured rewind: INTERP_BUFFER_MS +
 *  LAG_COMP_VIEW_EXTRA_CAP_MS + LAG_COMP_RTT_CAP_MS = 425 ms. */
export const LAG_COMP_HISTORY_MS = 450;
/** Cap (ms) on how much EXTRA view delay beyond INTERP_BUFFER_MS the server will
 *  honour from a shooter's reported render delay. The client renders remotes
 *  `base + jitterPad` behind, and the adaptive jitterPad grows to 220 ms on a
 *  bursty (WiFi power-save) link — a delay the old INTERP_BUFFER_MS-only rewind
 *  ignored, so exactly those players' well-aimed shots at a crosser landed
 *  `v · jitterPad` behind (0.54 m at 9 m/s with a 60 ms pad: a phantom miss past
 *  the 0.35 m torso). Capped well under the pad ceiling because every honoured
 *  millisecond is also "killed behind cover" window for the victim. */
export const LAG_COMP_VIEW_EXTRA_CAP_MS = 120;
/** Cap on the client-reported RTT the server will honor for lag-comp rewind
 *  (ms). Beyond this, extra rewind buys the shooter too much "shoot at a stale
 *  position" advantage (peeker's advantage / "killed behind cover" for the
 *  victim), and the history buffer is only LAG_COMP_HISTORY_MS deep anyway.
 *  INTERP_BUFFER_MS + this stays comfortably inside that window. */
export const LAG_COMP_RTT_CAP_MS = 250;
/** Lag-comp rewind for a BOT shooter, in ms.
 *
 *  Lag-comp exists to undo the shooter's OWN view lag: a human renders remotes
 *  `INTERP_BUFFER_MS` in the past and their fire packet reaches authority one
 *  RTT late, so their victims are rewound `INTERP_BUFFER_MS + RTT`. A bot has
 *  NEITHER — it reads live server state (no interpolation) and fires inside the
 *  same tick (no network hop). Its correct rewind is therefore ~0. The old code
 *  handed bots the human fallback (`INTERP_BUFFER_MS + lagCompExtraMs`, 105 ms),
 *  so a bot that aimed dead-on a target crossing at 9 m/s had its shot hit-checked
 *  a full body-width (~1 m) BEHIND where it aimed — an unintended second miss
 *  penalty stacked on top of the tuned aim error (`strafeErr`/`errorMul`), worst
 *  for high-skill bots whose scatter is tight enough that the 1 m offset whiffs
 *  entirely. Zeroing it lets the difficulty preset be the sole arbiter of bot
 *  accuracy against movers. A future iteration that finds hard bots too sharp
 *  against close crossers can dial this up toward one tick without touching the
 *  human path. `agent/bench-lagcomp.mjs` covers both. */
export const BOT_LAG_COMP_REWIND_MS = 0;
/** How far into the past the server rewinds a victim to match what the SHOOTER
 *  saw when they fired, in ms. The shooter renders remotes `viewDelayMs` behind
 *  their own clock (INTERP_BUFFER_MS on a clean link, + the adaptive jitterPad on
 *  a bursty one — reported by the client), and the shot itself is one network
 *  RTT late reaching authority, so the correct rewind is `viewDelay + RTT`. Before we have
 *  measured a HUMAN shooter's RTT (i.e. before their first Ping round-trip) we
 *  fall back to `fallbackExtraMs`, preserving the old fixed-slack behaviour.
 *  (Bots do NOT route through here — they read live state, so the caller gives
 *  them `BOT_LAG_COMP_REWIND_MS` directly.) RTT is clamped to
 *  [0, LAG_COMP_RTT_CAP_MS] so a laggy or hostile client can neither rewind past
 *  the history buffer nor hand itself unbounded lag-comp advantage. Pure +
 *  deterministic (invariant 7). */
export function lagCompRewindMs(rttMs, fallbackExtraMs, viewDelayMs) {
    const extra = rttMs === undefined || !Number.isFinite(rttMs)
        ? fallbackExtraMs
        : Math.min(LAG_COMP_RTT_CAP_MS, Math.max(0, rttMs));
    // The shooter's REPORTED total remote render delay (base + jitterPad); absent
    // or garbage = the floor alone (the old behaviour). Clamped to
    // [INTERP_BUFFER_MS, INTERP_BUFFER_MS + LAG_COMP_VIEW_EXTRA_CAP_MS].
    const view = viewDelayMs === undefined || !Number.isFinite(viewDelayMs)
        ? INTERP_BUFFER_MS
        : Math.min(INTERP_BUFFER_MS + LAG_COMP_VIEW_EXTRA_CAP_MS, Math.max(INTERP_BUFFER_MS, viewDelayMs));
    return view + extra;
}
// ---------------------------------------------------------------------------
// Input → ack drain (server-side per-player input queue)
// ---------------------------------------------------------------------------
//
// The server queues each client input (`onInput`) and drains the queue at the
// END of every sim tick, applying up to INPUT_MAX_DRAIN_PER_TICK inputs gated
// by a per-player token bucket (`sim.dtBudget`). These four tunables set the
// input→ack latency FLOOR — a named fluidity concern — so they live here with
// the other sim/networking tunables rather than hardcoded in the room. Moved
// out of GunGameRoom.ts 2026-07-30; agent/bench-input-drain.mjs drives THESE
// exact values to lock the property below.
//
// THE PROPERTY (verified 2026-07-30, re-derived 2026-10-21 for 60 Hz, benched):
//   The client sims at 60 Hz (SIM_DT_MS = 1000/60) and sends one input per sim
//   step. The server now ALSO ticks at 60 Hz (SIM_TICK_RATE), so a healthy
//   client feeds it ~1 input per tick — a 1:1 ratio (was 2:1 at the old 30 Hz
//   server). The budget refills at `tickMs * SLACK` and each input costs its
//   clamped dt (~16.7 ms). For the queue to stay backlog-free the refill must
//   cover the arrivals:
//       tickMs * SLACK  >=  (SIM_TICK_RATE / (1000/SIM_DT_MS)) inputs * dt
//       16.67 * SLACK   >=  1 * 16.67  =  16.67   →   SLACK >= 1.0
//   The break-even is SLACK >= 1.0 at BOTH rates (the arrival count and the tick
//   period scale together). SLACK = 1.15 keeps 15 % headroom, so a client stays
//   drained the same tick it sends (ack latency <= ~1 tick) AND a short backlog
//   from a client-side hitch is chewed down within a few ticks. Drop SLACK below
//   1.0 and the stream outpaces the drain: the queue grows to INPUT_QUEUE_CAP,
//   then `shift()` drops the OLDEST unprocessed input — a step the client already
//   predicted — forcing a permanent reconcile gap. That is the regression the
//   bench guards. `agent/bench-input-drain.mjs` imports SIM_TICK_RATE, so it
//   re-models the drain at whatever rate ships; re-run it after any change.
/** Max buffered inputs per player before the oldest is dropped (`onInput`). */
export const INPUT_QUEUE_CAP = 30;
/** Token-bucket refill multiplier on real tick time for the input drain.
 *  Must stay >= 1.0 (see the derivation above); 1.15 is the shipped headroom. */
export const INPUT_DT_BUDGET_SLACK = 1.15;
/** Cap on the accumulated drain budget (ms) — bounds how big a backlog burst a
 *  recovered client can replay in one tick, so it can't monopolize the tick. */
export const INPUT_DT_BUDGET_CAP_MS = 250;
/** Hard cap on inputs drained per player per tick, independent of the budget —
 *  a second guard against one backlogged client eating the whole sim tick. */
export const INPUT_MAX_DRAIN_PER_TICK = 8;
// ---------------------------------------------------------------------------
// Rooms
// ---------------------------------------------------------------------------
export const DEFAULT_ROOM_CAP = 16;
export const MAX_ROOM_CAP = 32;
export const ROOM_CODE_LENGTH = 6;
/** Human-friendly: no 0/O, 1/I/L ambiguity. */
export const ROOM_CODE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
/** Default round time for Gun Game (seconds). Highest tier wins on expiry. */
export const DEFAULT_ROUND_TIME_S = 600;
/** Default round time for ARENA (seconds). 0 = NO round timer: Mario Kart:
 *  Double Dash!! Bob-omb Blast has no time limit — the battle ends only when a
 *  kart reaches the points target (`arenaWinTarget`: 3 with 2 karts, 4 with
 *  3+). The lobby already allows "No timer", so no backstop is forced; an owner
 *  can still pick a clock, and `timeoutWin` then crowns the most-points kart.
 *  (Was 180: a Neon "timed most-stars" rule, replaced 2026-10 by the MKDD one.) */
export const ARENA_DEFAULT_ROUND_TIME_S = 0;
//# sourceMappingURL=constants.js.map